'use client';

import React, { useState } from 'react';
import { Plus, List, CalendarDays, Filter } from 'lucide-react';
import CalendarGrid from './CalendarGrid';
import PostListView from './PostListView';
import CreatePostModal from './CreatePostModal';

export type PostStatus = 'scheduled' | 'published' | 'failed' | 'draft';
export type PostPlatform = 'facebook' | 'instagram';
export type PostType = 'Image' | 'Video' | 'Carousel' | 'Story' | 'Reel';

export interface ScheduledPost {
  id: string;
  title: string;
  client: string;
  page: string;
  platform: PostPlatform;
  type: PostType;
  scheduledDate: string;
  scheduledTime: string;
  status: PostStatus;
  approvalStatus: 'approved' | 'pending' | 'rejected';
  copy: string;
}

export const scheduledPosts: ScheduledPost[] = [
  { id: 'post-001', title: 'Morning Ritual — New Blend Reveal', client: 'Bravo Coffee', page: 'Bravo Coffee Official', platform: 'instagram', type: 'Carousel', scheduledDate: '2026-08-06', scheduledTime: '9:00 AM', status: 'scheduled', approvalStatus: 'approved', copy: 'Introducing our limited-edition Autumn Harvest blend ☕ Notes of cinnamon, hazelnut, and dark chocolate…' },
  { id: 'post-002', title: 'Back to School Lookbook', client: 'UrbanWear Co.', page: 'UrbanWear Co.', platform: 'facebook', type: 'Image', scheduledDate: '2026-08-06', scheduledTime: '12:00 PM', status: 'scheduled', approvalStatus: 'approved', copy: 'Your campus style starts here 🎒 Shop the Back to School collection — 20% off all week.' },
  { id: 'post-003', title: 'Solar Savings Infographic', client: 'Solaris Solar', page: 'Solaris Solar Energy', platform: 'facebook', type: 'Image', scheduledDate: '2026-08-06', scheduledTime: '3:00 PM', status: 'scheduled', approvalStatus: 'pending', copy: 'Did you know the average homeowner saves $1,400/year with solar? Here\'s how it works 👇' },
  { id: 'post-004', title: 'FitTrack App Workout Demo', client: 'FitTrack Pro', page: 'FitTrack Pro', platform: 'instagram', type: 'Reel', scheduledDate: '2026-08-07', scheduledTime: '7:00 AM', status: 'scheduled', approvalStatus: 'approved', copy: 'Watch how FitTrack tracks your full-body workout in real time 💪 Link in bio to download free.' },
  { id: 'post-005', title: 'Organic Farm Story Series', client: 'Verde Organic', page: 'Verde Organic Foods', platform: 'instagram', type: 'Story', scheduledDate: '2026-08-07', scheduledTime: '11:00 AM', status: 'scheduled', approvalStatus: 'approved', copy: 'Behind the scenes at our partner farms this harvest season 🌿' },
  { id: 'post-006', title: 'Living Room Transformation', client: 'Luxe Living', page: 'Luxe Living Interiors', platform: 'instagram', type: 'Carousel', scheduledDate: '2026-08-08', scheduledTime: '2:00 PM', status: 'scheduled', approvalStatus: 'pending', copy: 'Before → After: A complete living room transformation using our Autumn 2026 collection ✨' },
  { id: 'post-007', title: 'Vet Recommended — New Dog Food', client: 'PetPal Supply', page: 'PetPal Supply', platform: 'facebook', type: 'Video', scheduledDate: '2026-08-08', scheduledTime: '10:00 AM', status: 'failed', approvalStatus: 'approved', copy: '9 out of 10 vets recommend PetPal\'s grain-free formula for active dogs 🐕 Shop now.' },
  { id: 'post-008', title: 'Tech Tip Tuesday — Smart Home', client: 'Nexus Tech', page: 'Nexus Technology', platform: 'facebook', type: 'Image', scheduledDate: '2026-08-09', scheduledTime: '10:00 AM', status: 'scheduled', approvalStatus: 'approved', copy: 'Tech Tip Tuesday 💡 Connect your entire home with Nexus Hub — works with Alexa, Google, and Siri.' },
  { id: 'post-009', title: 'Coffee Brewing Guide', client: 'Bravo Coffee', page: 'Bravo Coffee Official', platform: 'facebook', type: 'Carousel', scheduledDate: '2026-08-10', scheduledTime: '8:00 AM', status: 'scheduled', approvalStatus: 'approved', copy: '5 brewing methods, 5 different experiences. Which one is your morning ritual? ☕' },
  { id: 'post-010', title: 'Autumn Outfit Inspiration', client: 'UrbanWear Co.', page: 'UrbanWear Co.', platform: 'instagram', type: 'Reel', scheduledDate: '2026-08-11', scheduledTime: '6:00 PM', status: 'scheduled', approvalStatus: 'pending', copy: 'Fall fits are here 🍂 Swipe through our top 10 autumn looks for 2026.' },
  { id: 'post-011', title: 'PetPal Rescue Feature', client: 'PetPal Supply', page: 'PetPal Supply', platform: 'instagram', type: 'Image', scheduledDate: '2026-08-12', scheduledTime: '11:00 AM', status: 'scheduled', approvalStatus: 'approved', copy: 'Meet Biscuit 🐾 This month\'s rescue feature from our partner shelter. 10% of sales go to rescue orgs.' },
  { id: 'post-012', title: 'Weekend Sale Announcement', client: 'Luxe Living', page: 'Luxe Living Interiors', platform: 'facebook', type: 'Image', scheduledDate: '2026-08-13', scheduledTime: '9:00 AM', status: 'draft', approvalStatus: 'pending', copy: 'This weekend only — 25% off all sofas and accent chairs. In-store and online. Ends Sunday.' },
];

export default function ContentCalendarContent() {
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterClient, setFilterClient] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');

  const clients = ['all', ...Array.from(new Set(scheduledPosts.map((p) => p.client)))];

  const filteredPosts = scheduledPosts.filter((p) => {
    const matchClient = filterClient === 'all' || p.client === filterClient;
    const matchPlatform = filterPlatform === 'all' || p.platform === filterPlatform;
    return matchClient && matchPlatform;
  });

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Content Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {scheduledPosts.filter((p) => p.status === 'scheduled').length} posts scheduled · {scheduledPosts.filter((p) => p.scheduledDate === '2026-08-06').length} publishing today
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex bg-muted rounded-lg p-1">
            <button
              onClick={() => setView('calendar')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${view === 'calendar' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <CalendarDays size={13} />
              Calendar
            </button>
            <button
              onClick={() => setView('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${view === 'list' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <List size={13} />
              List
            </button>
          </div>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary text-sm">
            <Plus size={15} />
            Schedule Post
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Filter size={13} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground font-medium">Filter:</span>
        </div>
        <select
          value={filterClient}
          onChange={(e) => setFilterClient(e.target.value)}
          className="input-base text-xs py-1.5 w-auto pr-8 min-w-[140px]"
        >
          {clients.map((c) => (
            <option key={`cal-client-${c}`} value={c}>{c === 'all' ? 'All Clients' : c}</option>
          ))}
        </select>
        <select
          value={filterPlatform}
          onChange={(e) => setFilterPlatform(e.target.value)}
          className="input-base text-xs py-1.5 w-auto pr-8 min-w-[130px]"
        >
          <option value="all">All Platforms</option>
          <option value="facebook">Facebook</option>
          <option value="instagram">Instagram</option>
        </select>
        {(filterClient !== 'all' || filterPlatform !== 'all') && (
          <button onClick={() => { setFilterClient('all'); setFilterPlatform('all'); }} className="text-xs text-primary hover:underline">
            Clear
          </button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{filteredPosts.length} posts</span>
      </div>

      {/* Content */}
      {view === 'calendar' ? (
        <CalendarGrid posts={filteredPosts} onCreatePost={() => setShowCreateModal(true)} />
      ) : (
        <PostListView posts={filteredPosts} />
      )}

      <CreatePostModal open={showCreateModal} onClose={() => setShowCreateModal(false)} />
    </div>
  );
}